import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/axiosConfig';
import FinancialsFiltersModal from '../../components/FinancialsFiltersModal/FinancialsFiltersModal';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import useCompanyFilter from '../../hooks/useCompanyFilter';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { formatReportMoney } from '../../utils/financialReportUtils';
import { getDefaultFinancialsDateRange } from '../../utils/financialsDateRange';
import './FinancialsDashboard.css';
import '../../components/FinancialsFiltersModal/FinancialsFiltersModal.css';

function FinancialReports() {
  const defaultRange = getDefaultFinancialsDateRange();
  const { isLoading, withLoading } = usePageLoading(true);
  const {
    companies,
    selectedCompanyId,
    setSelectedCompanyId,
    companyQueryParam,
    companiesLoaded,
  } = useCompanyFilter();
  const [fromDate, setFromDate] = useState(defaultRange.fromDate);
  const [toDate, setToDate] = useState(defaultRange.toDate);
  const [showFilters, setShowFilters] = useState(false);
  const [summary, setSummary] = useState({
    incomeTotal: 0,
    expenseTotal: 0,
    billsBaseTotal: 0,
    billsGstTotal: 0,
    billsIncGstTotal: 0,
    outstandingBillsTotal: 0,
    netTotal: 0,
    incomeCount: 0,
    expenseCount: 0,
    billsCount: 0
  });

  const fetchSummary = useCallback(async (rangeFrom, rangeTo, companyParams) => {
    const response = await api.get('/financials/summary', {
      params: {
        fromDate: rangeFrom,
        toDate: rangeTo,
        ...companyParams,
      }
    });
    setSummary(response.data);
  }, []);

  useEffect(() => {
    if (!companiesLoaded) {
      return;
    }

    withLoading(async () => {
      await fetchSummary(fromDate, toDate, companyQueryParam);
    }).catch((error) => {
      const message = error.response?.data?.Error || error.message || 'Error loading financial summary';
      alert(message);
    });
  }, [fromDate, toDate, selectedCompanyId, companiesLoaded, fetchSummary, withLoading]);

  const companyLabel = useMemo(() => {
    if (!selectedCompanyId) {
      return 'All companies';
    }
    return companies.find((company) => String(company.id) === String(selectedCompanyId))?.name || 'Company';
  }, [companies, selectedCompanyId]);

  const maxChartValue = useMemo(() => {
    return Math.max(
      summary.incomeTotal,
      summary.expenseTotal,
      summary.billsBaseTotal,
      summary.billsGstTotal,
      summary.billsIncGstTotal,
      Math.abs(summary.outstandingBillsTotal),
      Math.abs(summary.netTotal),
      1
    );
  }, [summary]);

  const donutStyle = useMemo(() => {
    const income = summary.incomeTotal;
    const expense = summary.expenseTotal;
    const bills = summary.billsIncGstTotal;
    const total = income + expense + bills;

    if (total <= 0) {
      return { background: '#ececec' };
    }

    const incomePct = (income / total) * 100;
    const expensePct = (expense / total) * 100;
    const expenseEnd = incomePct + expensePct;

    return {
      background: `conic-gradient(
        #34c759 0 ${incomePct}%,
        #ff6b57 ${incomePct}% ${expenseEnd}%,
        #4f8cff ${expenseEnd}% 100%
      )`
    };
  }, [summary]);

  const getBarWidth = (value) => {
    const width = Math.max((Math.abs(value) / maxChartValue) * 100, value !== 0 ? 4 : 0);
    return `${width}%`;
  };

  const handleApplyFilters = (filters) => {
    setFromDate(filters.fromDate);
    setToDate(filters.toDate);
    setSelectedCompanyId(filters.companyId || '');
  };

  const handleClearFilters = (filters) => {
    handleApplyFilters(filters);
  };

  return (
    <PageLoadingArea isLoading={isLoading}>
      <div className="financials-dashboard">
        <div className="financials-dashboard-header">
          <div>
            <h1>Reports</h1>
            <p className="financials-dashboard-subtitle">
              Overview of income, expense, and billed totals
            </p>
            <div className="financials-active-filters">
              <span className="financials-active-filter-chip">
                {formatDateDDMMYYYY(fromDate)} – {formatDateDDMMYYYY(toDate)}
              </span>
              {companies.length > 0 && (
                <span className="financials-active-filter-chip">{companyLabel}</span>
              )}
            </div>
          </div>
          <div className="financials-dashboard-header-actions">
            <button
              type="button"
              className="financials-filter-trigger"
              onClick={() => setShowFilters(true)}
            >
              Filters
            </button>
          </div>
        </div>

        <section className="financials-stat-section">
          <div className="financials-stat-grid financials-stat-grid-bills">
            <article className="financials-stat-card bills">
              <div className="financials-stat-label">Bills (excl. GST)</div>
              <div className="financials-stat-value">{formatReportMoney(summary.billsBaseTotal)}</div>
              <div className="financials-stat-meta">{summary.billsCount} bill{summary.billsCount === 1 ? '' : 's'}</div>
            </article>
            <article className="financials-stat-card gst">
              <div className="financials-stat-label">GST amount</div>
              <div className="financials-stat-value">{formatReportMoney(summary.billsGstTotal)}</div>
              <div className="financials-stat-meta">Total GST on bills</div>
            </article>
            <article className="financials-stat-card bills-inc-gst">
              <div className="financials-stat-label">Bills incl. GST</div>
              <div className="financials-stat-value">{formatReportMoney(summary.billsIncGstTotal)}</div>
              <div className="financials-stat-meta">Invoice totals with GST</div>
            </article>
            <article className="financials-stat-card income">
              <div className="financials-stat-label">Income</div>
              <div className="financials-stat-value">{formatReportMoney(summary.incomeTotal)}</div>
              <div className="financials-stat-meta">{summary.incomeCount} entr{summary.incomeCount === 1 ? 'y' : 'ies'}</div>
            </article>
          </div>

          <div className="financials-stat-grid financials-stat-grid-summary">
            <article className="financials-stat-card outstanding">
              <div className="financials-stat-label">Outstanding bills</div>
              <div className="financials-stat-value">{formatReportMoney(summary.outstandingBillsTotal)}</div>
              <div className="financials-stat-meta">Bills incl. GST minus income</div>
            </article>
            <article className="financials-stat-card expense">
              <div className="financials-stat-label">Expense</div>
              <div className="financials-stat-value">{formatReportMoney(summary.expenseTotal)}</div>
              <div className="financials-stat-meta">{summary.expenseCount} entr{summary.expenseCount === 1 ? 'y' : 'ies'}</div>
            </article>
            <article className="financials-stat-card net">
              <div className="financials-stat-label">Net income</div>
              <div className="financials-stat-value">{formatReportMoney(summary.netTotal)}</div>
              <div className="financials-stat-meta">Income minus expense</div>
            </article>
          </div>
        </section>

        <section className="financials-charts-grid">
          <article className="financials-chart-card">
            <h2>Comparison chart</h2>
            <p className="financials-chart-hint">Visual comparison of income, expense, and billed amounts</p>

            <div className="financials-bar-row">
              <div className="financials-bar-label">Bills (excl. GST)</div>
              <div className="financials-bar-track">
                <div className="financials-bar-fill bills" style={{ width: getBarWidth(summary.billsBaseTotal) }} />
              </div>
              <div className="financials-bar-value">{formatReportMoney(summary.billsBaseTotal)}</div>
            </div>

            <div className="financials-bar-row">
              <div className="financials-bar-label">GST amount</div>
              <div className="financials-bar-track">
                <div className="financials-bar-fill gst" style={{ width: getBarWidth(summary.billsGstTotal) }} />
              </div>
              <div className="financials-bar-value">{formatReportMoney(summary.billsGstTotal)}</div>
            </div>

            <div className="financials-bar-row">
              <div className="financials-bar-label">Bills incl. GST</div>
              <div className="financials-bar-track">
                <div className="financials-bar-fill bills-inc-gst" style={{ width: getBarWidth(summary.billsIncGstTotal) }} />
              </div>
              <div className="financials-bar-value">{formatReportMoney(summary.billsIncGstTotal)}</div>
            </div>

            <div className="financials-bar-row">
              <div className="financials-bar-label">Income</div>
              <div className="financials-bar-track">
                <div className="financials-bar-fill income" style={{ width: getBarWidth(summary.incomeTotal) }} />
              </div>
              <div className="financials-bar-value">{formatReportMoney(summary.incomeTotal)}</div>
            </div>

            <div className="financials-bar-row">
              <div className="financials-bar-label">Outstanding</div>
              <div className="financials-bar-track">
                <div className="financials-bar-fill outstanding" style={{ width: getBarWidth(summary.outstandingBillsTotal) }} />
              </div>
              <div className="financials-bar-value">{formatReportMoney(summary.outstandingBillsTotal)}</div>
            </div>

            <div className="financials-bar-row">
              <div className="financials-bar-label">Expense</div>
              <div className="financials-bar-track">
                <div className="financials-bar-fill expense" style={{ width: getBarWidth(summary.expenseTotal) }} />
              </div>
              <div className="financials-bar-value">{formatReportMoney(summary.expenseTotal)}</div>
            </div>

            <div className="financials-bar-row">
              <div className="financials-bar-label">Net income</div>
              <div className="financials-bar-track">
                <div className="financials-bar-fill net" style={{ width: getBarWidth(summary.netTotal) }} />
              </div>
              <div className="financials-bar-value">{formatReportMoney(summary.netTotal)}</div>
            </div>
          </article>

          <article className="financials-chart-card financials-breakdown-card">
            <h2>Breakdown</h2>
            <p className="financials-chart-hint">Share of income, expense, and bills incl. GST in this period</p>

            <div className="financials-donut-wrap">
              <div className="financials-donut" style={donutStyle} aria-hidden="true" />

              <div className="financials-legend">
                <div className="financials-legend-item">
                  <span className="financials-legend-dot" style={{ background: '#34c759' }} />
                  Income
                </div>
                <div className="financials-legend-item">
                  <span className="financials-legend-dot" style={{ background: '#ff6b57' }} />
                  Expense
                </div>
                <div className="financials-legend-item">
                  <span className="financials-legend-dot" style={{ background: '#4f8cff' }} />
                  Bills incl. GST
                </div>
              </div>
            </div>

            <div className="financials-combined-total">
              <span>Combined total</span>
              <strong>{formatReportMoney(summary.incomeTotal + summary.expenseTotal + summary.billsIncGstTotal)}</strong>
            </div>
          </article>
        </section>
      </div>

      <FinancialsFiltersModal
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        mode="reports"
        values={{
          fromDate,
          toDate,
          companyId: selectedCompanyId,
          clientId: '',
          projectId: '',
        }}
        companies={companies}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />
    </PageLoadingArea>
  );
}

export default FinancialReports;
